import { CdkDragHandle } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  ViewEncapsulation,
} from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxIconButton } from '../icon-button/icon-button';
import { HxIcon } from '../icon/icon';
import { HxTooltipDirective } from '../tooltip/tooltip';

type ToggleAction = 'expand' | 'collapse';
type SectionStatus = 'completed' | 'inprogress' | 'pending';

type SectionStatusPresentation = {
  label: string;
  icon: string;
};

const SECTION_STATUS_PRESENTATION: Record<SectionStatus, SectionStatusPresentation> = {
  completed: {
    label: 'Completed',
    icon: 'ccs-check2',
  },
  inprogress: {
    label: 'In progress',
    icon: 'ccs-clock',
  },
  pending: {
    label: 'Pending',
    icon: 'ccs-exclamation-lg',
  },
};

@Component({
  selector: 'hx-section',
  standalone: true,
  templateUrl: './section.html',
  styleUrls: ['./section.css', './section-tokens.css'],
  imports: [HxIcon, CdkDragHandle, HxTooltipDirective, HxIconButton],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'hx-section',
  },
})
export class HxSection extends HxBaseControl {
  private readonly host = inject(ElementRef<HTMLElement>);

  // Inputs (fully controlled by parent)
  readonly title = input<string | undefined>('');
  readonly subTitle = input<string | undefined>('');
  readonly titleIcon = input<string | null>(null);
  readonly subTitleIcon = input<string | null>(null);
  readonly subTitleStatus = input<SectionStatus | null>(null);
  readonly hasSubSections = input<boolean>(false);
  readonly expandedAll = input<boolean>(true);
  readonly onToggleAll = input<((action: ToggleAction) => void) | null>(null);

  /**
   * Whether to show a drag handle icon for drag-drop operations. When enabled, displays a draggable handle icon on the
   * left side of the section header.
   *
   * @default false
   */
  readonly dragHandleEnabled = input<boolean>(false);

  /**
   * Whether to display the expand/collapse toggle icon when subsections are present.
   *
   * @default false
   */
  readonly showToggleIcon = input<boolean>(false);

  /**
   * Whether to display a chevron icon next to the title for expand/collapse functionality when subsections are present.
   *
   * @default true
   */
  readonly showChevron = input<boolean>(true);

  // Derived presentation
  protected readonly isExpandedAll = computed(() => !!this.expandedAll());
  protected readonly toggleLabel = computed(() =>
    this.isExpandedAll() ? 'Collapse Subsections' : 'Expand Subsections'
  );
  protected readonly toggleIcon = computed(() => (this.isExpandedAll() ? 'ccs-chevron-down' : 'ccs-chevron-right'));
  protected readonly toggleArrowIcon = computed(() =>
    this.isExpandedAll() ? 'ccs-arrows-angle-contract' : 'ccs-arrows-angle-expand'
  );
  protected readonly subTitleStatusPresentation = computed(() => {
    const status = this.subTitleStatus();
    return status ? SECTION_STATUS_PRESENTATION[status] : null;
  });
  // Event handler: relay to parent callback
  protected handleToggleAll(): void {
    const cb = this.onToggleAll();
    if (typeof cb === 'function') {
      const action: ToggleAction = this.isExpandedAll() ? 'collapse' : 'expand';
      cb(action);
    }
  }
}
